import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import BookConsultationButton from "@/components/BookConsultationButton";
import { 
  Phone, PhoneCall, Clock, Users, DollarSign, CheckCircle, ArrowRight,
  Zap, Calendar, MessageSquare, Shield, Star, Home, CloudRain,
  AlertTriangle, Hammer, FileText, Camera, ShieldCheck
} from "lucide-react";

const AIReceptionistRoofingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.title = "AI Receptionist for Roofers Dallas | Roofing Virtual Receptionist | 24/7 Roofing Answering Service DFW";
  }, []);

  const stats = [
    { value: "24/7", label: "Storm Call Response", icon: Clock },
    { value: "Unlimited", label: "Storm Surge Capacity", icon: CloudRain },
    { value: "<30s", label: "Lead Response Time", icon: Zap },
    { value: "$0", label: "Missed Storm Calls", icon: DollarSign }
  ];

  const roofingServices = [
    { icon: CloudRain, name: "Storm Damage", desc: "Hail damage calls, wind damage, emergency tarping, insurance claims" },
    { icon: Home, name: "Roof Replacement", desc: "Full replacement quotes, material options, financing inquiries" },
    { icon: Hammer, name: "Repairs", desc: "Leak repairs, shingle replacement, flashing issues" },
    { icon: FileText, name: "Insurance Claims", desc: "Claim assistance, adjuster scheduling, documentation" },
    { icon: Camera, name: "Inspections", desc: "Free inspections, annual checkups, real estate inspections" },
    { icon: ShieldCheck, name: "Maintenance", desc: "Gutter cleaning, preventive maintenance, warranty work" }
  ];

  const features = [
    { 
      icon: CloudRain, 
      title: "Storm Surge Handling", 
      desc: "After hail hits Dallas, phones explode. AI handles unlimited calls simultaneously—no busy signals, no voicemail, every lead captured while competitors drown." 
    },
    { 
      icon: Calendar, 
      title: "Instant Inspection Booking", 
      desc: "Books free roof inspections into your calendar. Captures address, damage type, and insurance info before your team arrives." 
    },
    { 
      icon: FileText, 
      title: "Insurance Claim Support", 
      desc: "Walks homeowners through the claims process, captures policy numbers, schedules adjuster coordination, and sets expectations." 
    },
    { 
      icon: AlertTriangle, 
      title: "Emergency Response", 
      desc: "Recognizes emergency situations—active leaks, structural damage, tree damage. Dispatches your emergency crew immediately." 
    },
    { 
      icon: Camera, 
      title: "Lead Qualification", 
      desc: "Gathers roof age, square footage, material preferences, budget, and timeline for replacement quotes. Filters tire-kickers from serious buyers." 
    },
    { 
      icon: Shield, 
      title: "Service Area Routing", 
      desc: "Knows your service zones. Captures all in-area leads, politely refers out-of-area calls, and routes to specific crews by region." 
    }
  ];

  const comparisons = [
    { item: "Office Staff", traditional: "$35,000-$45,000/year", ai: "$199-$449/month" },
    { item: "Storm Surge Capacity", traditional: "Overwhelmed/Missed", ai: "Unlimited simultaneous" },
    { item: "After-Hours Storm Calls", traditional: "Lost to competitors", ai: "100% captured" },
    { item: "Response Time", traditional: "Minutes to hours", ai: "Instant 24/7" },
    { item: "Lead Quality Data", traditional: "Varies by staff", ai: "Consistent, complete" },
    { item: "Spanish-Speaking", traditional: "Limited/None", ai: "Fluent Spanish" }
  ];

  const faqs = [
    { 
      q: "How does AI handle post-storm call surges?", 
      a: "After a Dallas hail storm, roofing companies can get 10-50x normal call volume. Human staff gets overwhelmed—busy signals, voicemail, and lost leads to faster competitors. Our AI handles unlimited simultaneous calls. Every single caller gets immediate answers, inspection scheduling, and information capture. One roofer told us they captured 340 leads after a storm while competitors' phones went to voicemail."
    },
    { 
      q: "Can AI help with insurance claim questions?",
      a: "Yes. The AI is trained on the typical roofing insurance claims process. It explains how claims work, captures policy information, answers common questions about deductibles and coverage, and sets realistic expectations. For complex questions, it schedules a callback from your claims specialist with all the details already collected."
    },
    { 
      q: "How does it qualify roof replacement leads?",
      a: "For replacement inquiries, the AI gathers: roof age, approximate square footage, current material type, desired materials, timeline (urgent vs. planning), financing interest, and budget range. Your estimators receive qualified leads with complete info for accurate quotes. Price shoppers get basic info without wasting your sales team's time."
    },
    { 
      q: "What about emergency roof situations?",
      a: "The AI recognizes emergencies—active leaks causing interior damage, structural damage from fallen trees, or dangerous situations. It captures the address and situation details while simultaneously alerting your emergency crew via call, text, and app notification. Your team can be rolling in minutes with full context."
    },
    { 
      q: "Does it integrate with roofing CRMs and software?",
      a: "We integrate with JobNimbus, AccuLynx, JobProgress, Roofr, and other roofing industry platforms. Leads are automatically created with full details, inspections are scheduled in your system, and all data syncs seamlessly. No double-entry, no missed information."
    },
    { 
      q: "How does AI handle follow-ups for inspections?",
      a: "After booking an inspection, the AI sends confirmation texts with date, time, and what to expect. It sends reminders 24 hours before and can reschedule if needed. For no-shows or cancellations, it automatically rebooking or captures the reason. This systematic approach dramatically reduces no-shows and keeps your crews productive."
    }
  ];

  const generateSchemas = () => {
    return [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "AI Receptionist for Roofing Companies",
        "description": "24/7 AI-powered virtual receptionist for roofing companies in Dallas-Fort Worth. Storm surge handling, inspection scheduling, insurance claim support.",
        "provider": { "@type": "LocalBusiness", "name": "Digital Media Ninja" },
        "areaServed": ["Dallas", "Fort Worth", "Plano", "Arlington", "Frisco", "McKinney"],
        "serviceType": "AI Virtual Receptionist for Roofers"
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a }
        }))
      },
      {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": "AI Roofing Receptionist Service",
        "offers": { "@type": "AggregateOffer", "lowPrice": "199", "highPrice": "449", "priceCurrency": "USD" },
      }
    ];
  };

  const schemas = generateSchemas();

  return (
    <div className="min-h-screen bg-black" data-testid="ai-receptionist-roofing-page">
      <Helmet>
        <title>AI Receptionist for Roofers Dallas | 24/7 Roofing Answering Service | Storm Surge Ready</title>
        <meta name="description" content="AI receptionist for Dallas roofers. Handle storm surge calls, book inspections 24/7, support insurance claims. JobNimbus & AccuLynx integration. Never miss a hail damage lead. Free demo!" />
        <meta name="keywords" content="AI receptionist roofers Dallas, roofing answering service, roofing virtual receptionist DFW, storm damage phone answering, roofing company AI, hail damage calls, roofing lead capture" />
        <link rel="canonical" href="https://digitalmedianinja.com/ai-receptionist-roofers" />
        <meta property="og:title" content="AI Receptionist for Roofers Dallas | Storm Surge Ready 24/7" />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        {schemas.map((schema, idx) => (
          <script key={idx} type="application/ld+json">{JSON.stringify(schema)}</script>
        ))}
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600/20 via-transparent to-red-900/10"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-full">
                <Home className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-sm font-medium">Built for Roofing Contractors</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
                AI Receptionist for <span className="text-red-400">Roofing Companies</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-xl">
                Capture every storm damage lead. AI handles unlimited calls during hail surges, books inspections 24/7, and supports insurance claims—while competitors go to voicemail.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <BookConsultationButton 
                  meetingType="general"
                  size="lg"
                  className="bg-red-500 hover:bg-red-600 text-white font-semibold px-8"
                >
                  Schedule Demo <ArrowRight className="w-5 h-5 ml-2" />
                </BookConsultationButton>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" /> Call Us Now
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <Card className="bg-white/5 border-red-500/20 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
                      <Home className="w-8 h-8 text-red-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Roofing Dispatch AI</h3>
                      <p className="text-red-400">Storm Surge Ready</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {["Unlimited storm call capacity", "JobNimbus & AccuLynx integration", "Insurance claim support", "24/7 inspection scheduling"].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-white/10">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 text-red-400 mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Roofing Services We Support</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roofingServices.map((service, i) => (
              <Card key={i} className="bg-white/5 border-white/10 hover:border-red-500/50 transition-all">
                <CardContent className="p-6">
                  <service.icon className="w-10 h-10 text-red-400 mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">{service.name}</h3>
                  <p className="text-gray-400">{service.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white/5">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Features for Roofing Contractors</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="p-6 rounded-xl bg-black/50 border border-white/10">
                <feature.icon className="w-10 h-10 text-red-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Roofing Reception Cost Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-6 text-gray-400">Expense</th>
                  <th className="text-center py-4 px-6 text-gray-400">Traditional</th>
                  <th className="text-center py-4 px-6 text-red-400">AI Receptionist</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, i) => (
                  <tr key={i} className="border-b border-white/10">
                    <td className="py-4 px-6 text-white">{row.item}</td>
                    <td className="py-4 px-6 text-center text-gray-400">{row.traditional}</td>
                    <td className="py-4 px-6 text-center text-red-400 font-semibold">{row.ai}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-white/10 rounded-lg overflow-hidden">
                <button className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/5" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span className="text-white font-medium">{faq.q}</span>
                  <ArrowRight className={`w-5 h-5 text-red-400 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
                </button>
                {openFaq === i && <div className="px-6 pb-4"><p className="text-gray-400">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learn More Section */}
      <section className="py-16 border-t border-white/10">
        <div className="container-custom">
          <div className="bg-gradient-to-r from-red-500/10 to-red-600/5 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Complete Guide to AI Receptionists for Roofers
                </h2>
                <p className="text-gray-300 mb-6">
                  Learn everything about implementing AI reception for your roofing company—from handling storm surge calls
                  to integrating with JobNimbus and Acculynx, plus ROI calculations during storm season.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-red-400" />
                    Storm surge call handling
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-red-400" />
                    Roofing CRM integrations guide
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-red-400" />
                    Insurance info capture
                  </li>
                </ul>
                <Link to="/blog/ai-receptionist-for-roofers">
                  <Button className="bg-red-500 hover:bg-red-600 text-white font-semibold">
                    Read the Complete Guide <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/5 rounded-xl p-6 border border-red-500/20">
                  <div className="flex items-center gap-3 mb-4">
                    <Home className="w-8 h-8 text-red-400" />
                    <div>
                      <div className="text-white font-semibold">12 Minute Read</div>
                      <div className="text-gray-400 text-sm">Comprehensive Guide</div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm text-gray-400">
                    <div>✓ Storm Response Workflow</div>
                    <div>✓ Emergency Call Escalation</div>
                    <div>✓ Inspection Scheduling</div>
                    <div>✓ FAQ Section with Schema</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-red-600/20 to-red-900/20">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Dominate Storm Season?</h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-8">Join Dallas roofers using AI to capture every lead and book more inspections.</p>
          <BookConsultationButton meetingType="aiReceptionist" size="lg" className="bg-red-500 hover:bg-red-600 text-white font-semibold px-8">
            Schedule Your Free Demo
          </BookConsultationButton>
        </div>
      </section>
    </div>
  );
};

export default AIReceptionistRoofingPage;
